// Author: Axel Antoine
// mail: ax.antoine@gmail.com
// website: https://axantoine.com
// 17/03/2021

// Loki, Inria project-team with Université de Lille
// within the Joint Research Unit UMR 9189 CNRS-Centrale
// Lille-Université de Lille, CRIStAL.
// https://loki.lille.inria.fr

// LICENCE: Licence.md

import { Vector3 } from 'three';
import type { Face } from './Face';
import { Halfedge } from './Halfedge';

const _u = new Vector3();
let _idCount = 0;

export interface adjacenceBoundaries {
  incomingHalfedge: Halfedge;
  outgoingHalfedge: Halfedge;
  previousVertex: Vertex;
  nextVertex: Vertex;
  angle: number;
}

export class Vertex {
  /** Vertex position */
  readonly position: Vector3 = new Vector3();
  readonly color: Vector3 = new Vector3();

  /** Reference to one halfedge starting from the vertex */
  halfedge: Halfedge | null = null;
  customData: number = 0;

  id: number;
  renderId: number;

  constructor(renderId: number) {
    this.id = _idCount;
    _idCount++;
    this.renderId = renderId;
  }

  /**
   * Returns a generator of free halfedges starting from this vertex.
   * @param start The halfedge to start, default is vertex halfedge
   */
  *freeHalfedgesOutLoop(start = this.halfedge) {
    for (const halfedge of this.loopCW(start)) {
      if (halfedge.isFree()) {
        yield halfedge;
      }
    }
    return null;
  }

  /**
   * Returns a generator of free halfedges arriving to this vertex.
   * @param start The halfedge to start, default is vertex halfedge
   */
  *freeHalfedgesInLoop(start = this.halfedge) {
    for (const halfedge of this.loopCW(start)) {
      if (halfedge.twin.isFree()) {
        yield halfedge.twin;
      }
    }
    return null;
  }

  /**
   * Returns a generator of boundary halfedges starting from this vertex.
   * @param start The halfedge to start, default is vertex halfedge
   */
  *boundaryHalfedgesOutLoop(start = this.halfedge) {
    for (const halfedge of this.loopCW(start)) {
      if (halfedge.isBoundary()) {
        yield halfedge;
      }
    }
    return null;
  }

  /**
   * Returns a generator of boundary halfedges arriving to this vertex.
   * @param start The halfedge to start, default is vertex halfedge
   */
  *boundaryHalfedgesInLoop(start = this.halfedge) {
    for (const halfedge of this.loopCW(start)) {
      if (halfedge.twin.isBoundary()) {
        yield halfedge.twin;
      }
    }
    return null;
  }

  /**
   * Returns whether the vertex is free, i.e. on of its ongoing halfedge has no
   * face.
   *
   * @ref https://kaba.hilvi.org/homepage/blog/halfedge/halfedge.htm
   *
   * @returns `true` if free, `false` otherwise
   */
  isFree() {
    if (this.isIsolated()) {
      return true;
    }
    for (const halfEdge of this.loopCW()) {
      if (halfEdge.isFree()) {
        return true;
      }
    }
    return false;
  }

  isIsolated() {
    return this.halfedge === null;
  }

  commonFacesWithVertex(other: Vertex) {
    const faces = new Array<Face>();
    for (const halfedge of this.loopCW()) {
      if (halfedge.face && halfedge.face.hasVertex(other)) {
        faces.push(halfedge.face);
      }
    }
    return faces;
  }

  /**
   * Checkes whether the vertex matches the given position
   *
   * @param      {Vector3}  position           The position
   * @param      {number}   [tolerance=1e-10]  The tolerance
   * @return     {boolean}
   */
  matchesPosition(position: Vector3, tolerance = 1e-10): boolean {
    _u.subVectors(position, this.position);
    return _u.length() < tolerance;
  }

  /**
   * Returns the halfedge going from *this* vertex to *other* vertex if any.
   * @param other The other vertex
   * @returns `HalfEdge` if found, `null` otherwise.
   */
  getHalfedgeToVertex(other: Vertex): Halfedge | null {
    for (const halfEdge of this.loopCW()) {
      if (halfEdge.twin.vertex === other) {
        return halfEdge;
      }
    }
    return null;
  }

  isConnectedToVertex(other: Vertex) {
    return this.getHalfedgeToVertex(other) !== null;
  }

  /**
   * Returns a generator of halfedges starting from this vertex in CW order.
   * @param start The halfedge to start looping, default is vertex halfedge
   */
  *loopCW(start = this.halfedge) {
    //TODO remove that seen
    if (start && start.vertex === this) {
      let curr: Halfedge = start;
      const seen = new Set<Halfedge>();
      do {
        if (seen.has(curr)) break; // safety guard against corrupt rings
        seen.add(curr);
        yield curr;
        curr = curr.twin.next;
      } while (curr !== start);
    }
    return null;
  }

  /**
   * Returns a generator of halfedges starting from this vertex in CCW order.
   * @param start The halfedge to start, default is vertex halfedge
   */
  *loopCCW(start = this.halfedge) {
    if (start && start.vertex === this) {
      let curr: Halfedge = start;
      const seen = new Set<Halfedge>();
      do {
        if (seen.has(curr)) break; // safety guard against corrupt rings
        seen.add(curr);
        yield curr;
        curr = curr.prev.twin;
      } while (curr != start);
    }
    return null;
  }

  static resetIdCount() {
    _idCount = 0;
  }

  /**
   * Returns a generator of all vertices connected to this vertex.
   */
  *adjacentVertices(start = this.halfedge) {
    if (start && start.vertex === this) {
      let curr: Halfedge = start;
      const seen = new Set<Halfedge>();
      do {
        if (seen.has(curr)) break; // safety guard against corrupt rings
        seen.add(curr);
        yield curr.twin.vertex; // The vertex at the other end of the edge
        curr = curr.twin.next;
      } while (curr !== start);
    }
    return null;
  }

  *oneRing() {
    yield* this.adjacentVertices();
    return null;
  }

  /**
   * Finds up to `n` adjacent vertices from the given start vertex using BFS.
   * @param start The starting vertex.
   * @param n The number of adjacent vertices to find.
   * @returns A generator yielding up to `n` adjacent vertices.
   */
  public *findNAdjacentVertices(n: number): Generator<Vertex, null, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const start = this;
    const visited = new Set<Vertex>();
    const queue: Vertex[] = [];

    visited.add(start);
    queue.push(start);

    while (queue.length > 0 && n > 0) {
      const current = queue.shift()!;
      for (const neighbor of current.adjacentVertices()) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
          yield neighbor;
          n--;
          if (n <= 0) {
            return null;
          }
        }
      }
    }
    return null;
  }

  /**
   * Returns BFS rings of neighbors around this vertex.
   * rings[0] = 1st ring (immediate neighbors), rings[1] = 2nd ring, ...
   * Stops early if frontier becomes empty.
   */
  public getRings(n: number): Array<Array<Vertex>> {
    const rings: Array<Array<Vertex>> = [];
    if (n <= 0) return rings;
    const visited = new Set<Vertex>();
    visited.add(this);

    let frontier: Vertex[] = [this];

    for (let depth = 1; depth <= n; depth++) {
      const nextFrontier: Vertex[] = [];
      const ring: Vertex[] = [];
      for (const v of frontier) {
        for (const nb of v.adjacentVertices()) {
          if (visited.has(nb)) continue;
          visited.add(nb);
          ring.push(nb);
          nextFrontier.push(nb);
        }
      }
      rings.push(ring);
      frontier = nextFrontier;
      if (frontier.length === 0) break;
    }

    return rings;
  }

  public computeVertexNormal(): Vector3 {
    const normal = new Vector3();
    let count = 0;
    for (const he of this.loopCW()) {
      if (he.face) {
        const faceNormal = new Vector3();
        he.face.getNormal(faceNormal);
        normal.add(faceNormal);
        count++;
      }
    }
    if (count > 0) {
      normal.divideScalar(count);
    }
    return normal.normalize();
  }

  /**
   * Returns boundary halfedges and adjacent boundary vertices for this vertex.
   * This method finds one incoming and one outgoing boundary halfedge, along with
   * the adjacent boundary vertices (previous and next in the boundary loop).
   * Also calculates the interior angle at this vertex.
   *
   * If the first boundary halfedge pair has an existing edge between previous and next vertices,
   * it tries to find alternative boundary halfedge pairs.
   *
   * @returns Object containing:
   *   - incomingHalfedge: boundary halfedge arriving to this vertex
   *   - outgoingHalfedge: boundary halfedge starting from this vertex
   *   - previousVertex: vertex at the start of the incoming boundary halfedge
   *   - nextVertex: vertex at the end of the outgoing boundary halfedge
   *   - angle: interior angle at this vertex in radians
   *   Returns null if this vertex is not on a boundary or no valid pair is found
   */
  getBoundaryHalfedgesAndVerticesInfo(): adjacenceBoundaries | null {
    if (!this.isFree()) return null;
    // Collect all incoming and outgoing boundary halfedges
    const incomingBoundaryHalfedges: Halfedge[] = [];
    for (const he of this.boundaryHalfedgesInLoop()) {
      if (he.face === null) {
        incomingBoundaryHalfedges.push(he);
      }
    }

    const outgoingBoundaryHalfedges: Halfedge[] = [];
    for (const he of this.boundaryHalfedgesOutLoop()) {
      if (he.face === null) {
        outgoingBoundaryHalfedges.push(he);
      }
    }

    if (incomingBoundaryHalfedges.length === 0 || outgoingBoundaryHalfedges.length === 0) {
      return null;
    }

    // Try different combinations of incoming and outgoing boundary halfedges
    for (const incomingHalfedge of incomingBoundaryHalfedges) {
      for (const outgoingHalfedge of outgoingBoundaryHalfedges) {
        const previousVertex = incomingHalfedge.vertex;
        const nextVertex = outgoingHalfedge.twin.vertex;

        // Check if boundary edge already exists between previous and next vertices
        const boundaryEdgeExists = Array.from(previousVertex.freeHalfedgesOutLoop()).some((he) => he.twin.vertex === nextVertex);

        if (boundaryEdgeExists) {
          continue; // Try next combination
        }

        // Calculate the interior angle at this vertex
        // Create vectors from current vertex to previous and next vertices
        const vec1 = new Vector3().subVectors(previousVertex.position, this.position).normalize();
        const vec2 = new Vector3().subVectors(nextVertex.position, this.position).normalize();

        // Simple angle calculation using normalized vectors
        let angle = Math.acos(Math.max(-1, Math.min(1, vec1.dot(vec2))));

        // Determine if this is a reflex angle (> 180°) using cross product
        const cross = new Vector3().crossVectors(vec1, vec2).normalize();

        // Get a reference face normal to determine which side is "inside"
        let meshNormal = new Vector3();
        let faceFound = false;

        // Try to get face normal from twin halfedges (since boundary halfedges have null faces)
        if (incomingHalfedge.twin.face) {
          incomingHalfedge.twin.face.getNormal(meshNormal);
          faceFound = true;
        } else if (outgoingHalfedge.twin.face) {
          outgoingHalfedge.twin.face.getNormal(meshNormal);
          faceFound = true;
        }

        if (faceFound) {
          // If cross product and mesh normal point in the same direction,
          // this means we have a concave angle (> 180°) from the hole's perspective
          if (cross.dot(meshNormal) > 0) {
            // Convert to the reflex angle (> 180°)
            angle = 2 * Math.PI - angle;
          }
        }

        return {
          incomingHalfedge,
          outgoingHalfedge,
          previousVertex,
          nextVertex,
          angle: (angle * 180) / Math.PI,
        };
      }
    }

    // No valid boundary halfedge pair found
    return null;
  }

  public getAdjacenceBoundaryVertices(): Array<Vertex> {
    const result = new Array<Vertex>();

    for (const he of this.boundaryHalfedgesInLoop()) {
      if (he.face === null) {
        result.push(he.vertex);
      }
    }

    for (const he of this.boundaryHalfedgesOutLoop()) {
      if (he.face === null) {
        result.push(he.twin.vertex);
      }
    }
    return result;
  }

  public getBoundaryAngle(prev: Vector3, center: Vector3, next: Vector3): number {
    const vec1 = new Vector3().subVectors(prev, center).normalize();
    const vec2 = new Vector3().subVectors(next, center).normalize();

    // Handle degenerate cases
    if (vec1.lengthSq() < 1e-10 || vec2.lengthSq() < 1e-10) {
      return 0;
    }

    // Calculate angle using dot product (always gives [0, π])
    const cosAngle = Math.max(-1, Math.min(1, vec1.dot(vec2)));
    let angle = Math.acos(cosAngle);

    // Use cross product to determine if we need the reflex angle
    const cross = new Vector3().crossVectors(vec1, vec2);

    // If cross product length is near zero, vectors are parallel
    if (cross.lengthSq() < 1e-10) {
      return cosAngle > 0 ? 0 : Math.PI; // 0° or 180°
    }

    // For consistent orientation, you might want to check against a reference normal
    // and convert to reflex angle if needed, similar to getBoundaryHalfedgesAndVerticesInfo

    return angle; // Returns [0, π] range
  }
}
